/**
 * Client-side logger for browser environments.
 * Uses console methods but respects log levels.
 *
 * Log level is controlled by NEXT_PUBLIC_LOG_LEVEL env var:
 * - "debug" (default in development): Shows all logs
 * - "info": Shows info, warn, error
 * - "warn" (default in production): Shows warn, error only
 * - "error": Shows error only
 */

type LogLevel = "debug" | "info" | "warn" | "error";

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function getLogLevel(): LogLevel {
  // Check for explicit env var first
  const envLevel = process.env.NEXT_PUBLIC_LOG_LEVEL as LogLevel | undefined;
  if (envLevel && LOG_LEVELS[envLevel] !== undefined) {
    return envLevel;
  }

  // Default: debug in development, warn in production
  return process.env.NODE_ENV === "development" ? "debug" : "warn";
}

const currentLevel = getLogLevel();
const currentLevelValue = LOG_LEVELS[currentLevel];

interface ClientLogger {
  debug: (message: string, data?: Record<string, unknown>) => void;
  info: (message: string, data?: Record<string, unknown>) => void;
  warn: (message: string, data?: Record<string, unknown>) => void;
  error: (message: string, data?: Record<string, unknown>) => void;
}

function createClientLogger(context: string): ClientLogger {
  const prefix = `[${context}]`;

  return {
    debug: (message: string, data?: Record<string, unknown>) => {
      if (currentLevelValue <= LOG_LEVELS.debug) {
        if (data) {
          console.debug(prefix, message, data);
        } else {
          console.debug(prefix, message);
        }
      }
    },
    info: (message: string, data?: Record<string, unknown>) => {
      if (currentLevelValue <= LOG_LEVELS.info) {
        if (data) {
          console.info(prefix, message, data);
        } else {
          console.info(prefix, message);
        }
      }
    },
    warn: (message: string, data?: Record<string, unknown>) => {
      if (currentLevelValue <= LOG_LEVELS.warn) {
        if (data) {
          console.warn(prefix, message, data);
        } else {
          console.warn(prefix, message);
        }
      }
    },
    error: (message: string, data?: Record<string, unknown>) => {
      if (currentLevelValue <= LOG_LEVELS.error) {
        if (data) {
          console.error(prefix, message, data);
        } else {
          console.error(prefix, message);
        }
      }
    },
  };
}

export { createClientLogger, type ClientLogger };
